import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getCommunity } from '../../../../services/communityService';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getEventTypeDisplayName } from '../../../../utils/EventTypesUtil';

// Default event types to use if a community doesn't have specific ones defined
const DEFAULT_EVENT_TYPES = ['rehearsal', 'mass', 'concert', 'meeting', 'other'];

function EventDetailsTab({ eventData, setEventData, isMobile, userCommunities, onCommunityChange }) {
    const { t } = useLanguage();
    const [supportedEventTypes, setSupportedEventTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch supported event types when community changes
    useEffect(() => {
        async function fetchSupportedEventTypes() {
            if (!eventData.community_id) {
                // Reset to default event types if no community selected
                setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                return;
            }

            try {
                setLoading(true);
                const community = await getCommunity(eventData.community_id);
                
                if (community && community.supported_event_types) {
                    try {
                        // Parse the event types from the community
                        const types = JSON.parse(community.supported_event_types);
                        setSupportedEventTypes(types);
                        
                        // If current event_type is not in supported list, change to first supported type
                        if (types.length > 0 && !types.includes(eventData.event_type)) {
                            setEventData(prev => ({
                                ...prev,
                                event_type: types[0]
                            }));
                        }
                    } catch (error) {
                        setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                    }
                } else {
                    setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                }
            } catch (error) {
                setSupportedEventTypes(DEFAULT_EVENT_TYPES);
            } finally {
                setLoading(false);
            }
        }
        
        fetchSupportedEventTypes();
    }, [eventData.community_id]);

    // Input change handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEventData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Community select handler
    const handleCommunitySelect = (e) => {
        const newCommunityId = e.target.value === "" ? null : e.target.value;
        
        // If community change handler is provided, use it
        if (onCommunityChange) {
            onCommunityChange(eventData.community_id, newCommunityId);
        } else {
            // Otherwise, just update the state
            setEventData(prevData => ({
                ...prevData,
                community_id: newCommunityId
            }));
        }
    };

    // Styling
    const labelStyle = {
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        fontWeight: '500',
        marginBottom: '0.25rem'
    };
    
    const helpTextStyle = {
        fontSize: isMobile ? '0.7rem' : '0.75rem'
    };
    
    const inputFontSize = isMobile ? '0.75rem' : '0.85rem';

    return (
        <div className="event-details-tab">
            <div className="row g-2">
                {/* Event Title */}
                <div className="col-12 mb-2">
                    <label htmlFor="title" className="form-label" style={labelStyle}>
                        {t('events.modals.details.eventTitle')}*
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="title"
                        name="title"
                        value={eventData.title}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    />
                </div>

                {/* Date and Time in the same row */}
                <div className="col-md-6 col-sm-6 mb-2">
                    <label htmlFor="date" className="form-label" style={labelStyle}>
                        {t('events.modals.details.date')}*
                    </label>
                    <input
                        type="date"
                        className="form-control"
                        id="date"
                        name="date"
                        value={eventData.date}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    />
                </div>

                <div className="col-md-6 col-sm-6 mb-2">
                    <label htmlFor="time" className="form-label" style={labelStyle}>
                        {t('events.modals.details.time')}
                    </label>
                    <input
                        type="time"
                        className="form-control"
                        id="time"
                        name="time"
                        value={eventData.time}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    />
                </div>

                {/* Location */}
                <div className="col-12 mb-2">
                    <label htmlFor="location" className="form-label" style={labelStyle}>
                        {t('events.modals.details.location')}*
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="location"
                        name="location"
                        value={eventData.location}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    />
                </div>

                {/* Event Type - Dynamically populated based on community */}
                <div className="col-md-6 mb-2">
                    <label htmlFor="event_type" className="form-label" style={labelStyle}>
                        {t('events.modals.details.eventType')}
                    </label>
                    <select
                        className="form-select"
                        id="event_type"
                        name="event_type"
                        value={eventData.event_type}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                        disabled={loading || supportedEventTypes.length === 0}
                    >
                        {loading ? (
                            <option value="">{t('events.modals.details.loading')}</option>
                        ) : supportedEventTypes.length > 0 ? (
                            supportedEventTypes.map(type => (
                                <option key={type} value={type}>
                                    {getEventTypeDisplayName(type, t)}
                                </option>
                            ))
                        ) : (
                            <option value="other">{t('events.types.other')}</option>
                        )}
                    </select>
                    {eventData.community_id && supportedEventTypes.length === 0 && !loading && (
                        <div className="form-text mt-1 text-warning" style={helpTextStyle}>
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            {t('events.messages.noCommunityEventTypes')}
                        </div>
                    )}
                </div>

                {/* Visibility */}
                <div className="col-md-6 mb-2">
                    <label htmlFor="visibility" className="form-label" style={labelStyle}>
                        {t('events.modals.details.visibility')}
                    </label>
                    <select
                        className="form-select"
                        id="visibility"
                        name="visibility"
                        value={eventData.visibility}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    >
                        <option value="private">{t('events.visibility.privateFull')}</option>
                        <option value="community">{t('events.visibility.community')}</option>
                        {/* <option value="public">{t('events.visibility.public')}</option> */}
                    </select>
                    <div className="form-text mt-1" style={helpTextStyle}>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        {eventData.visibility === 'community' ?
                            t('events.visibility.visibleToAll') :
                            t('events.visibility.visibleToYou')}
                    </div>
                </div>

                {/* Community Select - Only show when visibility is set to community */}
                {eventData.visibility === 'community' && (
                    <div className="col-12 mb-2">
                        <label htmlFor="community_id" className="form-label" style={labelStyle}>
                            {t('events.modals.details.community')}*
                        </label>
                        <select
                            className="form-select"
                            id="community_id"
                            name="community_id"
                            value={eventData.community_id ? String(eventData.community_id) : ""}
                            onChange={handleCommunitySelect}
                            style={{ fontSize: inputFontSize }}
                        >
                            <option value="">{t('events.modals.details.selectCommunity')}</option>
                            {userCommunities && userCommunities.map(community => (
                                <option key={community.id} value={community.id}>
                                    {community.name}
                                </option>
                            ))}
                        </select>
                        {!eventData.community_id && (
                            <div className="form-text mt-1 text-warning" style={helpTextStyle}>
                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                {t('events.messages.selectCommunity')}
                            </div>
                        )}
                    </div>
                )}

                {/* Description */}
                <div className="col-12 mb-2">
                    <label htmlFor="description" className="form-label" style={labelStyle}>
                        {t('events.modals.details.description')}
                    </label>
                    <textarea
                        className="form-control"
                        id="description"
                        name="description"
                        rows={3}
                        value={eventData.description}
                        onChange={handleInputChange}
                        style={{ fontSize: inputFontSize }}
                    ></textarea>
                </div>

                {/* YouTube Recording Link */}
                <div className="col-12 mb-2">
                    <label htmlFor="youtube_link" className="form-label" style={labelStyle}>
                        {t('events.modals.details.videoLink')}
                    </label>
                    <input
                        type="url"
                        className="form-control"
                        id="youtube_link"
                        name="youtube_link"
                        value={eventData.youtube_link || ""}
                        onChange={handleInputChange}
                        placeholder="https://www.youtube.com/watch?v=..."
                        style={{ fontSize: inputFontSize }}
                    />
                    <div className="form-text mt-1" style={helpTextStyle}>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        {t('events.modals.details.videoLinkHelp')}
                    </div>
                </div>
            </div>

            <div className="text-muted mt-1" style={helpTextStyle}>
                * {t('events.modals.details.requiredFields')}
            </div>
        </div>
    );
}

export default EventDetailsTab;